import {
  Dialog,
  DialogActions,
  DialogContent,
  type DialogProps,
  DialogTitle,
  Fade,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { type FormEvent, forwardRef, type ReactElement, type ReactNode, type Ref, useCallback, useMemo } from 'react';
import { ANIMATION_TIMEOUT } from '@/common/timeout_constants';
import { useIsMobile } from '@/hooks/useIsMobile';
import CloseButton from '../button/close_button';
import { bottomSheetDialogProps, bottomSheetSx } from './bottom_sheet';
import { type DialogKey, useDialogStore } from './dialogStore';

const FadeTransition = forwardRef(function Transition(props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) {
  return <Fade ref={ref} {...props} timeout={ANIMATION_TIMEOUT} />;
});

export const dialogTopAnchorSx: SxProps<Theme> = {
  '& .MuiDialog-container': { alignItems: 'flex-start' },
  '& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)': { mt: '12vh' },
};

export interface BaseDialogProps {
  dialogKey: DialogKey;
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  banner?: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  paperMaxWidth?: number | string | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number | string>>;
  onClose?: () => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  dialogProps?: Partial<DialogProps>;
  anchorTop?: boolean;
  /** Below `md`: full screen, or a sheet sliding up from the bottom for short action forms. */
  mobileLayout?: 'fullScreen' | 'sheet';
}

export function BaseDialog({
  dialogKey,
  title,
  children,
  actions,
  banner,
  maxWidth,
  paperMaxWidth,
  onClose,
  onSubmit,
  dialogProps,
  anchorTop,
  mobileLayout = 'fullScreen',
}: BaseDialogProps) {
  const isMobile = useIsMobile();
  const isSheet = isMobile && mobileLayout === 'sheet';
  const isOpen = useDialogStore((state) => !!state.openDialogs[dialogKey]);
  const closeDialog = useDialogStore((state) => state.closeDialog);

  const handleDialogClose = () => {
    if (onClose) {
      onClose();
    }
    closeDialog(dialogKey);
  };

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (onSubmit) {
        onSubmit(event);
      }
    },
    [onSubmit]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI types paper slot as HTMLDivElement, but we conditionally render as <form>
  const slotProps: any = useMemo(
    () => ({
      paper: {
        elevation: 0,
        ...(paperMaxWidth != null && { sx: { maxWidth: paperMaxWidth } }),
        ...(onSubmit && {
          component: 'form',
          onSubmit: handleSubmit,
        }),
      },
      transition: {
        timeout: ANIMATION_TIMEOUT,
        // Focus the first editable field once the open transition finishes. `autoFocus` alone races
        // with the native click that refocuses the trigger button; after that, MUI's FocusTrap pulls
        // focus to the dialog container instead of the field. Running after the transition wins the race.
        // Skipped on sheets: it would pop the on-screen keyboard over the content.
        onEntered: (node: HTMLElement) => {
          if (isSheet) return;
          node
            .querySelector<HTMLInputElement | HTMLTextAreaElement>(
              'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])'
            )
            ?.focus();
        },
      },
      backdrop: {
        timeout: ANIMATION_TIMEOUT,
        transitionDuration: ANIMATION_TIMEOUT,
        style: {
          transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
        },
      },
    }),
    [onSubmit, handleSubmit, paperMaxWidth, isSheet]
  );

  const { sx: dialogPropsSx, ...restDialogProps } = dialogProps ?? {};

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      fullScreen={isMobile && !isSheet}
      maxWidth={paperMaxWidth != null ? false : maxWidth}
      slots={{
        transition: FadeTransition,
      }}
      slotProps={slotProps}
      sx={anchorTop ? dialogTopAnchorSx : dialogPropsSx}
      {...restDialogProps}
      {...(isSheet && {
        ...bottomSheetDialogProps,
        sx: [bottomSheetSx, ...(Array.isArray(dialogPropsSx) ? dialogPropsSx : [dialogPropsSx])],
      })}
    >
      {banner}
      <DialogTitle sx={{ position: 'relative', mb: 1 }}>
        {title}
        <CloseButton onClick={handleDialogClose} />
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}
